"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import { featureService } from "@/services/featureService";
import { Feature } from "@/types/feature.types";

export interface SubscriptionPlanFormData {
  name: string;
  price: number;
  features: { id: string; type: "boolean" | "number"; value: string }[];
}

interface SubscriptionPlanFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: SubscriptionPlanFormData;
  onFormDataChange: (data: SubscriptionPlanFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditMode?: boolean;
}

export function SubscriptionPlanFormDialog({
  open,
  onOpenChange,
  formData,
  onFormDataChange,
  onSave,
  onCancel,
  isEditMode = false,
}: SubscriptionPlanFormDialogProps) {
  const [availableFeatures, setAvailableFeatures] = useState<Feature[]>([]);
  const [isLoadingFeatures, setIsLoadingFeatures] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchFeatures = async () => {
        setIsLoadingFeatures(true);
        try {
          const features = await featureService.getAllFeatures();
          const activeFeatures = features.filter((f) => !f.isDeleted);
          setAvailableFeatures(activeFeatures);
          
          if (isEditMode && formData.features.length > 0) {
            // In edit mode: merge all available features with subscription's features
            // Features in subscription keep their values, new features get defaults
            const mergedFeatures = activeFeatures.map((availableFeature) => {
              const existingFeature = formData.features.find(
                (f) => f.id === availableFeature.id
              );
              
              if (existingFeature) {
                // Feature exists in subscription, use its current type and value
                // Ensure type is normalized and value is a string
                return {
                  id: existingFeature.id,
                  type: (existingFeature.type.toLowerCase() === "number" ? "number" : "boolean") as "boolean" | "number",
                  value: String(existingFeature.value),
                };
              } else {
                // Feature doesn't exist in subscription, add with default
                return {
                  id: availableFeature.id,
                  type: "boolean" as "boolean" | "number",
                  value: "false",
                };
              }
            });
            
            // Always update to ensure all available features are shown
            // This handles cases where new features were added to the system
            if (mergedFeatures.length > 0) {
              onFormDataChange({ ...formData, features: mergedFeatures });
            }
          } else if (!isEditMode) {
            // New plan: Auto-populate form with all features with default values
            // Only if form is empty
            if (formData.features.length === 0 && activeFeatures.length > 0) {
              const defaultFeatures = activeFeatures.map((f) => ({
                id: f.id,
                type: "boolean" as "boolean" | "number",
                value: "false", // Default boolean value
              }));
              onFormDataChange({ ...formData, features: defaultFeatures });
            }
          }
        } catch (error) {
          console.error("Failed to fetch features:", error);
        } finally {
          setIsLoadingFeatures(false);
        }
      };
      fetchFeatures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditMode, formData.features.length]);

  const updateField = <K extends keyof SubscriptionPlanFormData>(
    field: K,
    value: SubscriptionPlanFormData[K]
  ) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  const removeFeature = (index: number) => {
    updateField(
      "features",
      formData.features.filter((_, i) => i !== index)
    );
  };

  const updateFeature = (
    index: number,
    field: "id" | "type" | "value",
    value: string
  ) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = {
      ...updatedFeatures[index],
      [field]: value,
    };
    updateField("features", updatedFeatures);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Subscription Plan" : "Add New Subscription Plan"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the subscription plan with features and pricing"
              : "Create a new subscription plan with features and pricing"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="plan-name">Plan Name *</Label>
            <Input
              id="plan-name"
              value={formData.name}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="e.g., Silver, Gold, Platinum"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="plan-price">Monthly Price (QAR) *</Label>
            <Input
              id="plan-price"
              type="number"
              placeholder="Enter monthly price"
              value={formData.price}
              onChange={(e) =>
                updateField("price", parseFloat(e.target.value) || 0)
              }
              min="0"
              step="0.01"
            />
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label>Features</Label>
              <p className="text-sm text-muted-foreground">
                All features are shown with default values. Modify as needed.
              </p>
            </div>
            {isLoadingFeatures ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Loading features...
              </p>
            ) : (
              <div className="space-y-2 border rounded-md p-4">
                {formData.features.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No features available.
                  </p>
                ) : (
                  formData.features.map((feature, index) => {
                    const selectedFeature = availableFeatures.find(
                      (f) => f.id === feature.id
                    );
                    
                    return (
                      <div
                        key={feature.id || index}
                        className="flex items-start gap-2 p-2 border rounded-md"
                      >
                        <div className="flex-1 grid gap-2">
                          <div className="flex items-center gap-2">
                            <Label className="font-medium min-w-[150px]">
                              {selectedFeature?.name || "Unknown Feature"}
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select
                              value={feature.type}
                              onValueChange={(value) => {
                                const newType = value as "boolean" | "number";
                                // Reset value when type changes
                                const defaultValue = newType === "boolean" ? "false" : "0";
                                const updatedFeatures = [...formData.features];
                                updatedFeatures[index] = {
                                  ...updatedFeatures[index],
                                  type: newType,
                                  value: defaultValue,
                                };
                                updateField("features", updatedFeatures);
                              }}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="boolean">Boolean</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                              </SelectContent>
                            </Select>
                            {feature.type === "boolean" ? (
                              <Select
                                value={feature.value}
                                onValueChange={(value) =>
                                  updateFeature(index, "value", value)
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select value" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="true">True</SelectItem>
                                  <SelectItem value="false">False</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                placeholder="e.g., 5"
                                type="number"
                                value={feature.value}
                                onChange={(e) =>
                                  updateFeature(index, "value", e.target.value)
                                }
                                className="flex-1"
                              />
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFeature(index)}
                          title="Remove feature from plan"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onSave}>
            {isEditMode ? "Update Plan" : "Add Plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

